import { useState, memo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import FormControl from '@zenbusiness/zui/FormControl';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';
import { v1 as uuidv1 } from 'uuid';

import AutoCompleteInput from '#client/components/AutoCompleteInput';
import {
  ACCOUNT_PURPOSE_OPTIONS,
  ACCOUNT_PURPOSES_REQUIRING_DETAIL,
  UNIT_TEXT_DETAIL_MAX_LENGTH
} from '#client/constants/additionalInfo';
import addToString from '#client/utils/addToString';

import { StyledPopper } from './components/IndustryUIFieldVerticalList.styles';

import { isOptionEqualToValue, renderOption } from './utils/verticalListOptions';

const options = addToString(ACCOUNT_PURPOSE_OPTIONS);

const AccountPurposeUIField = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  dataChangeHandler = () => {},
  disabled,
  data
}) => {
  const [selectedOption, setSelectedOption] = useState(
    options.find((option) => option.value === data?.accountPurpose)
  );
  const [detailValue, setDetailValue] = useState(data?.accountPurposeDetail || '');
  // Gate validation errors on user interaction so in-progress applications don't show red on load.
  const [hasInteracted, setHasInteracted] = useState(false);
  const trackAccountPurposeChangeClick = useButtonPressedCallback({
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });

  const isDetailRequired = ACCOUNT_PURPOSES_REQUIRING_DETAIL.includes(selectedOption?.value);
  const showDetailError = hasInteracted && isDetailRequired && !detailValue.trim();

  // currentSelectedOption override avoids stale closure when called right after setSelectedOption.
  const onDataChanged = (event, option, currentSelectedOption = selectedOption) => {
    const enumValue = currentSelectedOption?.value;
    if (option || enumValue) {
      trackAccountPurposeChangeClick(event);
      field.setLoading();
    }
    // Per Unit V2, accountPurposeDetail is required for specific accountPurpose values.
    // Gate field.setSaved on that completeness check so submission stays blocked until
    // the detail is provided for triggering enum values.
    const trimmedDetail = (option ?? detailValue).trim();
    const isComplete =
      Boolean(enumValue) &&
      (!ACCOUNT_PURPOSES_REQUIRING_DETAIL.includes(enumValue) || Boolean(trimmedDetail));
    dataChangeHandler(
      !!option || !!enumValue,
      { accountPurpose: enumValue, accountPurposeDetail: trimmedDetail || null },
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave && isComplete, isError);
        forceUpdate();
      },
      field.key
    );
  };

  const handleChangeOption = (event, option) => {
    setSelectedOption(option);
    // Mark interacted when a triggering enum is picked so the error shows without waiting for blur.
    if (option?.value && ACCOUNT_PURPOSES_REQUIRING_DETAIL.includes(option.value)) {
      setHasInteracted(true);
    }
    onDataChanged(event, undefined, option);
  };

  const handleAccountPurposeDetailChange = (event) => {
    setDetailValue(event.target.value);
  };

  const handleAccountPurposeDetailBlur = (event) => {
    setHasInteracted(true);
    onDataChanged(event, event.target.value);
  };

  return (
    <FormControl
      fullWidth
      sx={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
      variant="outlined"
    >
      <AutoCompleteInput
        autoHighlight
        blurOnSelect
        disabled={disabled}
        getOptionLabel={(option) => option.toString()}
        isOptionEqualToValue={isOptionEqualToValue}
        ListboxProps={{
          style: {
            maxHeight: '400px',
            py: 2,
            borderRadius: 2
          }
        }}
        noOptionsText="No results. Try a new keyword."
        onChange={handleChangeOption}
        openOnFocus
        options={options}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              // Use for preventing auto suggestion from browser
              name: uuidv1()
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              ...params.InputProps
            }}
            label="Select"
          />
        )}
        renderOption={renderOption}
        value={selectedOption || null}
      />

      <Stack spacing={1.5}>
        <Text
          color="tertiary"
          size="md"
        >
          Please explain more detail around account purpose selection.
        </Text>

        <TextField
          disabled={disabled}
          error={showDetailError}
          helperText={showDetailError ? 'Details are required for this account purpose.' : ''}
          id="accountPurpose"
          inputProps={{
            maxLength: UNIT_TEXT_DETAIL_MAX_LENGTH
          }}
          label="Enter details"
          onBlur={handleAccountPurposeDetailBlur}
          onChange={handleAccountPurposeDetailChange}
          type="text"
          value={detailValue}
        />
      </Stack>
    </FormControl>
  );
};

AccountPurposeUIField.propTypes = {
  data: PropTypes.shape({
    accountPurpose: PropTypes.string,
    accountPurposeDetail: PropTypes.string
  }),
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    key: PropTypes.string
  }),
  disabled: PropTypes.bool,
  useFor: PropTypes.string
};

function areEqual(preProp, nextProp) {
  return (
    preProp.field === nextProp.field &&
    preProp.data === nextProp.data &&
    preProp.disabled === nextProp.disabled
  );
}

export default memo(AccountPurposeUIField, areEqual);
